import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileCheck2,
  Package,
  Pencil,
  Sparkles,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Masthead } from '@/components/Masthead';
import { SkeletonLine } from '@/components/Skeleton';
import { fmtDate, fmtInt } from '@/lib/format';
import {
  displayValue,
  fieldNeedsReview,
  formsApi,
  sourceLabel,
  statusLabel,
  useBuildFormPackage,
  useFormPackage,
  usePatchFormField,
  type FormFieldRow,
} from '@/lib/forms';

export default function FormPackageDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, error } = useFormPackage(id);
  const build = useBuildFormPackage(id ?? '');

  const pkg = data?.package;
  const documents = data?.documents ?? [];

  const { reviewCount, totalCount } = useMemo(() => {
    let needs = 0;
    let total = 0;
    for (const d of documents) {
      for (const f of d.fields) {
        total += 1;
        if (fieldNeedsReview(f)) needs += 1;
      }
    }
    return { reviewCount: needs, totalCount: total };
  }, [documents]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <Masthead kicker="Submission" title="Loading package…" />
        <div className="card flex flex-col gap-3 p-8">
          <SkeletonLine width="40%" />
          <SkeletonLine width="80%" />
          <SkeletonLine width="60%" />
        </div>
      </div>
    );
  }

  if (isError || !pkg) {
    return (
      <div className="flex flex-col gap-6">
        <Masthead kicker="Submission" title="Package not found" />
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          {error instanceof Error ? error.message : 'The package could not be loaded.'}
        </div>
        <Link to="/forms" className="inline-flex items-center gap-1 text-sm text-brand-700 hover:text-brand-800">
          <ArrowLeft className="h-4 w-4" /> Back to Forms
        </Link>
      </div>
    );
  }

  const canPackage = reviewCount === 0 && pkg.status !== 'packaged' && pkg.status !== 'delivered';
  const isPackaged = pkg.status === 'packaged' || pkg.status === 'delivered';

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/forms"
        className="inline-flex items-center gap-1 text-[0.8125rem] font-semibold text-ink-muted hover:text-ink"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All packages
      </Link>

      <Masthead
        kicker="Submission"
        title={`${pkg.legal_entity} → ${pkg.candidate_address}`}
        dateline={`RLP ${pkg.solicitation_number ?? '—'}${pkg.agency ? ` · ${pkg.agency}` : ''} · Updated ${fmtDate(pkg.updated_at)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {isPackaged && pkg.packaged_r2_key && (
              <a
                href={formsApi.downloadUrl(pkg.id)}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-brand-300 bg-brand-50 px-4 py-2 text-sm font-semibold text-brand-800 shadow-card hover:bg-brand-100"
              >
                <Download className="h-4 w-4" />
                Download PDF
              </a>
            )}
            <Button
              variant="primary"
              size="md"
              disabled={!canPackage || build.isPending}
              onClick={() => build.mutate()}
              data-testid="forms-build-package"
            >
              <Package className="h-4 w-4" />
              {build.isPending
                ? 'Packaging…'
                : isPackaged
                  ? 'Rebuild package'
                  : 'Package & render'}
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <Stat label="Status" value={statusLabel(pkg.status)} />
        <Stat label="Fields" value={`${fmtInt(reviewCount)} / ${fmtInt(totalCount)} need review`} />
        <Stat
          label="AI confidence"
          value={pkg.ai_confidence != null ? `${Math.round(pkg.ai_confidence * 100)}%` : '—'}
        />
        <Stat
          label="Packaged"
          value={pkg.packaged_at != null ? fmtDate(pkg.packaged_at) : 'Not yet'}
        />
      </div>

      {build.isError && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          Build failed: {build.error instanceof Error ? build.error.message : 'unknown'}
        </div>
      )}
      {build.data && !build.data.ok && (
        <div className="rounded-xl border border-danger-border bg-danger-soft px-5 py-4 text-sm text-danger-bold">
          {build.data.results
            .filter((r) => r.error)
            .map((r) => `${r.form_document_id}: ${r.error}`)
            .join(' · ') || 'Some documents failed to build.'}
        </div>
      )}

      {documents.map((doc) => (
        <DocumentSection
          key={doc.id}
          packageId={pkg.id}
          docTitle={prettyFormKey(doc.form_key)}
          docStatus={doc.status}
          fields={doc.fields}
        />
      ))}

      {documents.length === 0 && (
        <div className="card p-8 text-center text-sm text-ink-muted">
          The fill job is still running. Refresh in a few seconds.
        </div>
      )}
    </div>
  );
}

function DocumentSection({
  packageId,
  docTitle,
  docStatus,
  fields,
}: {
  packageId: string;
  docTitle: string;
  docStatus: string;
  fields: FormFieldRow[];
}) {
  const [showAll, setShowAll] = useState(false);
  const review = useMemo(() => fields.filter(fieldNeedsReview), [fields]);
  const settled = useMemo(
    () => fields.filter((f) => !fieldNeedsReview(f)),
    [fields]
  );

  return (
    <section className="card flex flex-col gap-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-rule pb-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-base font-bold tracking-head text-ink">{docTitle}</h2>
          <div className="text-[0.8125rem] text-ink-muted">
            {fmtInt(review.length)} to review · {fmtInt(settled.length)} settled
          </div>
        </div>
        <Badge tone={docStatus === 'ready' ? 'green' : docStatus === 'failed' ? 'red' : 'blue'} dot>
          {docStatus}
        </Badge>
      </header>

      {review.length === 0 && !showAll && (
        <div className="flex items-center justify-between rounded-lg bg-success-soft px-4 py-3 text-[0.8125rem] text-success-bold">
          <span className="inline-flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Every field is either rule-filled or operator-approved.
          </span>
          <button
            type="button"
            className="text-xs font-semibold underline-offset-2 hover:underline"
            onClick={() => setShowAll(true)}
          >
            Show all fields
          </button>
        </div>
      )}

      <ul className="flex flex-col divide-y divide-rule">
        {(showAll ? fields : review).map((f) => (
          <FormFieldEditor key={f.id} field={f} packageId={packageId} />
        ))}
      </ul>

      {showAll && review.length === 0 && (
        <button
          type="button"
          className="self-start text-xs font-semibold text-ink-muted underline-offset-2 hover:underline hover:text-ink"
          onClick={() => setShowAll(false)}
        >
          Collapse resolved fields
        </button>
      )}
      {!showAll && settled.length > 0 && review.length > 0 && (
        <button
          type="button"
          className="self-start text-xs font-semibold text-ink-muted underline-offset-2 hover:underline hover:text-ink"
          onClick={() => setShowAll(true)}
        >
          Also show {fmtInt(settled.length)} settled field{settled.length === 1 ? '' : 's'}
        </button>
      )}
    </section>
  );
}

function FormFieldEditor({
  field,
  packageId,
}: {
  field: FormFieldRow;
  packageId: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(() => displayValue(field) ?? '');
  const patch = usePatchFormField(packageId);

  const needsReview = fieldNeedsReview(field);
  const shown = displayValue(field);
  const confidence = field.confidence != null ? Math.round(field.confidence * 100) : null;

  const save = () => {
    const trimmed = draft.trim();
    patch.mutate(
      { fieldId: field.id, final_value: trimmed === '' ? null : trimmed },
      { onSuccess: () => setEditing(false) }
    );
  };

  const clearOverride = () => {
    patch.mutate({ fieldId: field.id, final_value: null });
  };

  return (
    <li className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <code className="text-[0.8125rem] font-semibold text-ink">{field.field_key}</code>
          <Badge tone={field.source === 'human' ? 'brand' : field.source === 'ai' ? 'violet' : 'neutral'}>
            {sourceLabel(field.source)}
          </Badge>
          {confidence != null && (
            <Badge tone={confidence >= 85 ? 'green' : 'yellow'}>
              <Sparkles className="h-3 w-3" /> {confidence}%
            </Badge>
          )}
          {needsReview && (
            <Badge tone="yellow" dot>
              <AlertTriangle className="h-3 w-3" /> review
            </Badge>
          )}
        </div>
        {field.rationale && (
          <p className="max-w-2xl text-[0.8125rem] text-ink-muted">{field.rationale}</p>
        )}
        {field.reviewed_at != null && (
          <div className="text-[0.6875rem] text-ink-dim">
            Reviewed {fmtDate(field.reviewed_at)}
            {field.reviewed_by ? ` · ${field.reviewed_by}` : ''}
          </div>
        )}
      </div>

      <div className="flex w-full max-w-md flex-col items-stretch gap-2 sm:items-end">
        {editing ? (
          <>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={Math.max(2, Math.min(6, Math.round(draft.length / 60)))}
              className="w-full rounded-lg border border-rule bg-surface px-3 py-2 font-mono text-[0.8125rem] text-ink focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-500/10"
              data-testid={`form-field-input-${field.id}`}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDraft(displayValue(field) ?? '');
                  setEditing(false);
                }}
                disabled={patch.isPending}
              >
                <X className="h-3.5 w-3.5" /> Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={save}
                disabled={patch.isPending}
                data-testid={`form-field-save-${field.id}`}
              >
                <FileCheck2 className="h-3.5 w-3.5" />
                {patch.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div
              className={
                'w-full whitespace-pre-wrap rounded-lg border px-3 py-2 font-mono text-[0.8125rem] ' +
                (shown == null || shown === ''
                  ? 'border-warn-border bg-warn-soft text-warn-bold italic'
                  : 'border-rule bg-surface-sunk text-ink')
              }
            >
              {shown == null || shown === '' ? 'No value proposed' : shown}
            </div>
            <div className="flex items-center justify-end gap-2">
              {field.final_value != null && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearOverride}
                  disabled={patch.isPending}
                  title="Revert to the AI/rule proposal"
                >
                  <X className="h-3.5 w-3.5" /> Clear override
                </Button>
              )}
              <Button
                variant={needsReview ? 'primary' : 'outline'}
                size="sm"
                onClick={() => {
                  setDraft(displayValue(field) ?? '');
                  setEditing(true);
                }}
                data-testid={`form-field-edit-${field.id}`}
              >
                <Pencil className="h-3.5 w-3.5" />
                {needsReview ? 'Fill in' : 'Edit'}
              </Button>
            </div>
          </>
        )}
        {patch.isError && (
          <div className="text-[0.75rem] text-danger-bold">
            Save failed: {patch.error instanceof Error ? patch.error.message : 'unknown'}
          </div>
        )}
      </div>
    </li>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card flex flex-col gap-1 px-5 py-4">
      <div className="text-[0.6875rem] font-medium uppercase tracking-[0.08em] text-ink-muted">
        {label}
      </div>
      <div className="text-base font-bold text-ink">{value}</div>
    </div>
  );
}

function prettyFormKey(key: string): string {
  if (key === 'gsa-527') return 'GSA Form 527 — Contractor Qualifications & Financial Information';
  return key;
}
